import { useState } from 'react';

import { useValidateTranscriptMutation } from '@teachur/http-client';

import { useUploadFile } from '@new/features/transcripts/shared/hooks/useUploadFile';
import { getErrorMessage } from '@new/lib';
import { mergeValidationResults, type EditableCourseRow } from './courseRows';

export type CourseValidationPhase = 'idle' | 'validating' | 'completed' | 'failed';

export type OfficialFileStatus = 'uploading' | 'validating' | 'done' | 'failed';

export interface OfficialFileEntry {
  key: string;
  file: File;
  fileId: number | null;
  status: OfficialFileStatus;
  error: string | null;
}

let entryKeySeq = 0;
const nextEntryKey = (): string => `of${++entryKeySeq}`;

export const useOfficialTranscriptValidation = (
  transcriptId: number,
  setRows: (updater: (rows: EditableCourseRow[]) => EditableCourseRow[]) => void
) => {
  const upload = useUploadFile();
  const validate = useValidateTranscriptMutation();

  const [files, setFiles] = useState<OfficialFileEntry[]>([]);

  const updateEntry = (key: string, patch: Partial<OfficialFileEntry>) => {
    setFiles((prev) => prev.map((entry) => (entry.key === key ? { ...entry, ...patch } : entry)));
  };

  const runValidation = async (key: string, file: File) => {
    updateEntry(key, { status: 'validating', error: null });
    const formData = new FormData();
    formData.append('file', file);

    try {
      const data = await validate.mutateAsync({ id: transcriptId, formData });
      setRows((prev) => mergeValidationResults(prev, data.results));
      updateEntry(key, { status: 'done' });
    } catch (err) {
      updateEntry(key, {
        status: 'failed',
        error: getErrorMessage(err, 'Something went wrong while analyzing this document.'),
      });
    }
  };

  const uploadAndValidate = async (key: string, file: File) => {
    const { id: uploadedId, error: uploadError } = await upload.uploadFileWithResult(file);
    if (uploadedId == null) {
      updateEntry(key, {
        status: 'failed',
        error: uploadError ?? 'Something went wrong while uploading this file.',
      });
      return;
    }
    updateEntry(key, { fileId: uploadedId });
    await runValidation(key, file);
  };

  const uploadOfficialFiles = async (newFiles: File[]) => {
    const entries = newFiles.map((file) => ({
      key: nextEntryKey(),
      file,
      fileId: null,
      status: 'uploading' as const,
      error: null,
    }));
    setFiles((prev) => [...prev, ...entries]);

    for (const entry of entries) {
      await uploadAndValidate(entry.key, entry.file);
    }
  };

  const removeOfficialFile = (key: string) => {
    setFiles((prev) => prev.filter((entry) => entry.key !== key));
  };

  const retryValidation = (key: string) => {
    const entry = files.find((e) => e.key === key);
    if (!entry) return;
    if (entry.fileId == null) {
      updateEntry(key, { status: 'uploading', error: null });
      uploadAndValidate(key, entry.file);
      return;
    }
    runValidation(key, entry.file);
  };

  const officialFiles = files
    .filter((entry) => entry.fileId != null)
    .map((entry) => ({ fileId: entry.fileId as number, label: entry.file.name }));

  const isFileUploading = files.some((entry) => entry.status === 'uploading');
  const validationPhase: CourseValidationPhase =
    files.length === 0
      ? 'idle'
      : files.some((entry) => entry.status === 'validating')
        ? 'validating'
        : files.some((entry) => entry.status === 'failed')
          ? 'failed'
          : 'completed';

  return {
    files,
    officialFiles,
    isFileUploading,
    validationPhase,
    uploadOfficialFiles,
    removeOfficialFile,
    retryValidation,
  };
};
